import { FC, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useFormik } from 'formik';
import UploadImage from '../../../../../../../_metronic/helpers/components/ImageUpload';
import axios from 'axios';
import { successToast, errorToast } from '../../../../../../../_metronic/helpers/components/Toaster';

interface UserTableModalProps {
    user: any;
    onClose: () => void;
    show: boolean;
}

interface ColorType {
    colorName: string;
    color: string;
}

interface Grade {
    id?: string;
    gradeName?: string;
    [key: string]: any;
}

export const ApprovedListingDetails: FC<UserTableModalProps> = ({ user, onClose, show }) => {
    const [quantity, setQuantity] = useState(user.orderDetail?.[0]?.items?.[0]?.quantity?.toString() || '');
    const [price, setPrice] = useState(user.orderDetail?.[0]?.items?.[0]?.price?.toString() || '');
    const [deliveryDate, setDeliveryDate] = useState(() => {
        const timestamp = user.deliveryDate;
        if (timestamp) {
            const date = new Date(timestamp);
            return date.toISOString().split('T')[0];
        }
        return '';
    });
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    // Dropdown states
    const [grades, setGrades] = useState<Grade[]>([]);
    const [colors, setColors] = useState<ColorType[]>([]);
    const [packaging, setPackaging] = useState<string[]>([]);
    const [selectedGrade, setSelectedGrade] = useState(user.orderDetail?.[0]?.items?.[0]?.grade || '');
    const [selectedColor, setSelectedColor] = useState(user.orderDetail?.[0]?.items?.[0]?.color || '');
    const [selectedPackaging, setSelectedPackaging] = useState(user.packagingType || '');

    const formik = useFormik({
        initialValues: {
            Image1: null,
            Image2: null
        },
        onSubmit: (values) => {
            console.log(values);
        }
    });

    const materialDocs = user.images?.filter(
        (img: any) => img.docType === 'MATERIAL_DOC' || img.docType === 'SALE_DOC'
    ) || [];

    // Fetch Grade, Color, and Packaging data
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                setLoading(true);

                // Get itemId from user data
                const itemId = user.orderDetail?.[0]?.items?.[0]?.itemId;

                if (itemId) {
                    // Fetch Grade based on itemId
                    const gradeRes = await fetch(
                        `https://pfc-backend-api.asmglobal.co.in/v1/api/grade/item/${itemId}`
                    );
                    const gradeData = await gradeRes.json();

                    if (gradeData && Array.isArray(gradeData.data)) {
                        setGrades(gradeData.data);
                    } else if (Array.isArray(gradeData)) {
                        setGrades(gradeData);
                    } else {
                        setGrades([]);
                    }
                }

                // Fetch Color
                const colorRes = await fetch(
                    'https://pfc-backend-api.asmglobal.co.in/v1/api/color'
                );
                const colorData = await colorRes.json();
                setColors(colorData.data || []);

                // Fetch Packaging
                const packRes = await fetch(
                    'https://pfc-backend-api.asmglobal.co.in/v1/api/packaging'
                );
                const packData = await packRes.json();
                setPackaging(packData.data || []);

            } catch (error) {
                console.error('Error fetching dropdown data:', error);
                errorToast('Error loading dropdown data');
            } finally {
                setLoading(false);
            }
        };

        if (user && show) {
            fetchDropdownData();
        }
    }, [user, show]);

    const handleApprove = async () => {
        try {
            setLoading(true);

            // Validate required inputs
            if (!quantity || !price || !deliveryDate || !selectedGrade || !selectedColor || !selectedPackaging) {
                errorToast('Please fill all required fields');
                setLoading(false);
                return;
            }

            // Convert delivery date to timestamp
            const deliveryDateTimestamp = new Date(deliveryDate).getTime();

            // Update orderDetail with new values
            const updatedOrderDetail = user.orderDetail.map((order: any) => ({
                ...order,
                items: order.items.map((item: any) => ({
                    ...item,
                    quantity: parseFloat(quantity),
                    price: parseFloat(price),
                    grade: selectedGrade,
                    color: selectedColor
                }))
            }));

            // Prepare images array
            const images: Array<{ docType: string; docUrl: string; docNumber: string }> = [];

            if (formik.values.Image1) {
                images.push({
                    docType: "VERIFIED_DOC",
                    docUrl: formik.values.Image1,
                    docNumber: ""
                });
            }

            if (formik.values.Image2) {
                images.push({
                    docType: "VERIFIED_DOC",
                    docUrl: formik.values.Image2,
                    docNumber: ""
                });
            }

            // Prepare payload
            const payload = {
                deliveryDate: deliveryDateTimestamp,
                packagingType: selectedPackaging,
                orderDetail: updatedOrderDetail,
                images: images,
                remarks: remarks || '',
                note: [
                    {
                        userId: user?.id,
                        noteType: "SALE_ORDER",
                        note: remarks || ''
                    }
                ]
                // note: {
                //     userId: user?.id,
                //     noteType: "PICKUP_POINT",
                //     note: remarks
                // }

            };

            // Get auth data from localStorage
            const authData = localStorage.getItem('kt-auth-react-v');
            let authToken = '';
            let pickupPointId = '';

            if (authData) {
                try {
                    const parsedAuth = JSON.parse(authData);
                    authToken = parsedAuth?.data?.token || '';
                    pickupPointId = parsedAuth?.data?.centreId || parsedAuth?.data?.franchiseId || '';
                } catch (e) {
                    console.error('Error parsing auth data:', e);
                }
            }

            if (!authToken) {
                errorToast('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            // Make API call
            await axios.put(
                `https://pfc-backend-api.asmglobal.co.in/v1/api/saleOrders/${user.id}/verify`,
                payload,
                {
                    headers: {
                        'accept': 'application/json',
                        'content-type': 'application/json',
                        'pickuppointid': pickupPointId,
                        'authorization': authToken,
                        'app-id': '3',
                        'client-id': '101212'
                    }
                }
            );

            successToast('Order approved successfully!');
            onClose();

        } catch (error: any) {
            console.error("Error:", error);
            errorToast(error?.response?.data?.message || 'Failed to approve order');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        try {
            setRejectLoading(true);

            const authData = localStorage.getItem('kt-auth-react-v');
            let authToken = '';
            let pickupPointId = '';

            if (authData) {
                try {
                    const parsedAuth = JSON.parse(authData);
                    authToken = parsedAuth?.data?.token || '';
                    pickupPointId = parsedAuth?.data?.centreId || parsedAuth?.data?.franchiseId || '';
                } catch (e) {
                    console.error('Error parsing auth data:', e);
                }
            }

            if (!authToken) {
                errorToast('Authentication token not found. Please login again.');
                setRejectLoading(false);
                return;
            }


            const payload = {
                remarks: remarks || '',
                note: [
                    {
                        userId: user?.id,
                        noteType: "SALE_ORDER",
                        note: remarks || ''
                    }
                ]
            }

            try {
                await axios.put(
                    `https://pfc-backend-api.asmglobal.co.in/v1/api/saleOrders/${user.id}/reject`, payload)
                successToast('Order rejected successfully!');
                onClose();
            } catch (err) {
                // Fallback to verify endpoint with status REJECTED if reject endpoint is unavailable
            }

        } catch (error: any) {
            console.error("Reject Error:", error);
            errorToast(error?.response?.data?.message || 'Failed to reject order');
        } finally {
            setRejectLoading(false);
        }
    };

    const isProcessing = loading || rejectLoading;

    return (
        <Modal
            className='modal fade'
            id='kt_modal_view_user'
            data-backdrop='static'
            tabIndex={-1}
            role='dialog'
            show={show}
            centered
            size="lg"
        >
            <div className="modal-content shadow-lg border-0 rounded-3">
                {/* Modal Header */}
                <div className="modal-header border-bottom-0 pb-0 pt-5 px-7 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <h3 className="modal-title fw-bolder text-gray-800 m-0 fs-3">Item Details & Verification</h3>
                        {user?.saleOrderId && (
                            <span className="badge badge-light-primary fw-bold fs-7 px-3 py-1">
                                Order #{user.saleOrderId}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-icon btn-sm btn-active-light-primary rounded-circle"
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        <i className="bi bi-x fs-1"></i>
                    </button>
                </div>

                <div className="modal-body scroll-y px-7 py-5">
                    {/* Quantity, Price, Delivery Date Section */}
                    <div className="card card-bordered p-4 mb-5 shadow-sm bg-white rounded-3">
                        <div className="fw-bold fs-6 text-primary mb-3 d-flex align-items-center">
                            <i className="bi bi-list-check me-2 fs-5"></i> Basic Specifications
                        </div>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Quantity</label>
                                <input
                                    type="number"
                                    className="form-control form-control-solid border placeholder-dark"
                                    value={quantity}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || parseFloat(value) >= 0) {
                                            setQuantity(value);
                                        }
                                    }}
                                    placeholder="Enter quantity"
                                    min="0"
                                    step="0.01"
                                    disabled={isProcessing}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Price (₹)</label>
                                <input
                                    type="number"
                                    className="form-control form-control-solid border placeholder-dark"
                                    value={price}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || parseFloat(value) >= 0) {
                                            setPrice(value);
                                        }
                                    }}
                                    placeholder="Enter price"
                                    min="0"
                                    step="0.01"
                                    disabled={isProcessing}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Delivery Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-solid border placeholder-dark"
                                    value={deliveryDate}
                                    onChange={(e) => setDeliveryDate(e.target.value)}
                                    disabled={isProcessing}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Grade, Color, Packaging Section */}
                    <div className="card card-bordered p-4 mb-5 shadow-sm bg-white rounded-3">
                        <div className="fw-bold fs-6 text-primary mb-3 d-flex align-items-center">
                            <i className="bi bi-palette me-2 fs-5"></i> Classification & Packaging
                        </div>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Grade</label>
                                <select
                                    className="form-select form-select-solid border text-gray-900"
                                    value={selectedGrade}
                                    onChange={(e) => setSelectedGrade(e.target.value)}
                                    disabled={isProcessing}
                                    required
                                >
                                    <option value="" className="text-gray-900">Select Grade</option>
                                    {grades.map((grade, index) => (
                                        <option key={grade.id || `grade-${index}`} value={grade.gradeName || ''} className="text-gray-900">
                                            {grade.gradeName || `Grade ${index + 1}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Color</label>
                                <select
                                    className="form-select form-select-solid border text-gray-900"
                                    value={selectedColor}
                                    onChange={(e) => setSelectedColor(e.target.value)}
                                    disabled={isProcessing}
                                    required
                                    style={{
                                        backgroundImage: selectedColor
                                            ? `url(${colors.find((c) => c.colorName === selectedColor)?.color})`
                                            : undefined,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 30px center',
                                        backgroundSize: '20px 20px',
                                        paddingRight: '45px'
                                    }}
                                >
                                    <option value="" className="text-gray-900">Select Color</option>
                                    {colors.map((color) => (
                                        <option
                                            key={color.colorName}
                                            value={color.colorName}
                                            className="text-gray-900"
                                        >
                                            {color.colorName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold text-gray-700 fs-7">Packaging</label>
                                <select
                                    className="form-select form-select-solid border text-gray-900"
                                    value={selectedPackaging}
                                    onChange={(e) => setSelectedPackaging(e.target.value)}
                                    disabled={isProcessing}
                                    required
                                >
                                    <option value="" className="text-gray-900">Select Packaging</option>
                                    {packaging.map((pack) => (
                                        <option key={pack} value={pack} className="text-gray-900">
                                            {pack}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>



                    {/* Material Documents & Upload Section */}
                    <div className="card card-bordered p-4 mb-2 shadow-sm rounded-3">
                        {materialDocs.length > 0 && (
                            <div className="mb-4">
                                <label className="fw-bold fs-6 text-gray-800 mb-3 d-block">Material Documents</label>
                                <div className="d-flex flex-wrap gap-3 justify-content-start">
                                    {materialDocs.map((doc: any, index: number) => (
                                        <div key={index} className="position-relative border rounded p-1 bg-white">
                                            <img
                                                src={doc.docUrl}
                                                alt={`${doc.docType} Document`}
                                                className="img-fluid cursor-pointer rounded"
                                                style={{
                                                    width: '140px',
                                                    height: '140px',
                                                    objectFit: 'cover'
                                                }}
                                                onClick={() => window.open(doc.docUrl, '_blank')}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="fw-bold fs-6 text-gray-800 mb-3 d-block">Upload Verification Images (Optional)</label>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <UploadImage name='Image1' formik={formik} label='Upload Image 1' />
                                </div>
                                <div className="col-md-6">
                                    <UploadImage name='Image2' formik={formik} label='Upload Image 2' />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Remarks / Comment Section */}
                    {user?.id && <div className="card card-bordered p-4 mb-5 shadow-sm rounded-3">
                        <div className="fw-bold fs-6 text-primary mb-2 d-flex align-items-center">
                            <i className="bi bi-chat-left-text me-2 fs-5"></i> Remark / Comment
                        </div>
                        <textarea
                            className='form-control bg-white border placeholder-dark'
                            rows={3}
                            placeholder="Enter remarks or feedback for approval/rejection..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            disabled={isProcessing}
                        />
                    </div>}
                </div>

                {/* Modal Footer Actions */}
                <div className="modal-footer border-top-0 pt-0 px-7 pb-5 gap-2">
                    <button
                        type="button"
                        className="btn btn-light me-auto"
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        Close
                    </button>
                    {user?.id && <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleReject}
                        disabled={isProcessing}
                        style={{ minWidth: '110px' }}
                    >
                        {rejectLoading ? (
                            <span className="indicator-progress d-inline-block">
                                Rejecting...{' '}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        ) : (
                            <span>Reject</span>
                        )}
                    </button>}
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleApprove}
                        disabled={isProcessing}
                        style={{ minWidth: '110px' }}
                    >
                        {loading ? (
                            <span className="indicator-progress d-inline-block">
                                Approving...{' '}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        ) : (
                            <span>Approve</span>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};